/**
 * Endpoints #77-#85 — AI-assisted comment drafting (SPEC `Comment Generation`).
 *
 * Comment generation is a **fenced sidecar**: it writes `ai_conversations`/`ai_messages` and its own
 * two settings keys, and nothing in monitoring or watching imports it. This file is the whole
 * contract the SPA sees — the `ai` settings blob (§4.1 registry), the Settings-tab read model, the
 * chat read model and the four write bodies.
 *
 * Two shapes are deliberately reused rather than re-declared: `ScraperFieldDTO` (a backend's config
 * card is rendered by the same component as a scraper's, so a new backend needs zero frontend work)
 * and the `scraperConfig.<pluginId>` storage convention (`aiConfig.<backendId>`, same secret rules —
 * plaintext at rest per ADR-010, masked at the API edge, omitted-means-keep, `''` clears).
 *
 * No value envelopes appear here: the chat ships no computed numbers, only strings the client
 * renders verbatim (§12.5).
 */
import { z } from 'zod';
/* ------------------------------------------------------------------ limits */
/** Ceiling on each stored prompt (#81). A template is a paragraph, not a document. */
export const AI_PROMPT_MAX_CHARS = 8000;
/** Ceiling on one chat message the user sends (#83/#84) — and on a preset's prompt, which is one. */
export const AI_MESSAGE_MAX_CHARS = 4000;
/** Ceiling on a preset's button label. A preset name is a word or two, not a sentence. */
export const AI_PRESET_NAME_MAX_CHARS = 40;
/** How many presets the composer row will carry. Past this the row stops being a row. */
export const AI_PRESETS_MAX = 20;
/* ----------------------------------------------------------- config storage */
export const AI_CONFIG_PREFIX = 'aiConfig.';
/** Key of a backend's config block, e.g. `aiConfig.anthropic` (the `scraperConfigKey` mirror). */
export function aiConfigKey(backendId) {
    return `${AI_CONFIG_PREFIX}${backendId}`;
}
/* -------------------------------------------------------------- settings blob */
/**
 * The backend bound on a fresh install (SPEC §5). Not a contract with the registry — an id whose
 * backend is not installed is a legal stored value, reported as `ready: false` by #77.
 */
const AI_DEFAULT_BACKEND_ID = 'anthropic';
/** Default system prompt — editable copy, not contract (SPEC §5). */
export const AI_DEFAULT_SYSTEM_PROMPT = 'You help write thoughtful LinkedIn comments. Be concise and specific to the post. Write in plain language, no emojis or hashtags unless asked.';
/**
 * Default post-context template — editable copy, not contract (SPEC §5). `{{...}}` tokens are
 * substituted server-side against the post; an unknown token renders as an empty string.
 *
 * Rendered once at conversation start and appended to the frozen system prompt, so it *describes*
 * the post rather than asking for anything — the asking is a preset's job, or the user's.
 *
 * `{{EMBEDDED_TEXT}}` is deliberately absent from the shipped copy: it fills on quote posts only,
 * and a labelled line that is empty on every other shape reads as a bug. It stays available.
 */
export const AI_DEFAULT_CONTEXT_TEMPLATE = [
    'The LinkedIn post under discussion:',
    '',
    'Author: {{AUTHOR_NAME}} — {{AUTHOR_HEADLINE}}',
    'Type: {{POST_TYPE}}',
    '',
    '{{POST_TEXT}}',
].join('\n');
/** The presets a fresh install starts with — editable copy, not contract (SPEC §5). */
export function aiDefaultPresets() {
    return [
        { name: 'Comment', prompt: 'Write a comment for this post.' },
        { name: 'Shorter', prompt: 'Make it shorter.' },
        { name: 'Longer', prompt: 'Make it longer, with one concrete detail from the post.' },
    ];
}
export function aiSettingsDefault() {
    return {
        activeBackend: AI_DEFAULT_BACKEND_ID,
        systemPrompt: AI_DEFAULT_SYSTEM_PROMPT,
        contextTemplate: AI_DEFAULT_CONTEXT_TEMPLATE,
        presets: aiDefaultPresets(),
    };
}
/** Frozen default for the §4.1 registry table; mutable copies come from the factory above. */
export const AI_SETTINGS_DEFAULT = aiSettingsDefault();
/**
 * A stored prompt is non-empty by construction — #81 refuses a blank one — so a blank that reaches
 * the decoder is corruption (a hand-edited database, a truncated write), and falling back to the
 * shipped default beats sending an empty `system` to a provider.
 */
const storedPrompt = (fallback) => z.string().trim().min(1).max(AI_PROMPT_MAX_CHARS).catch(fallback).default(fallback);
/** One stored preset. Both halves are trimmed and non-empty for the same reason a prompt is. */
const StoredPresetSchema = z.strictObject({
    name: z.string().trim().min(1).max(AI_PRESET_NAME_MAX_CHARS),
    prompt: z.string().trim().min(1).max(AI_MESSAGE_MAX_CHARS),
});
/**
 * Lenient decode of the stored blob: every field falls back on its own rather than throwing, so a
 * hand-edited database can never crash a boot (same contract as the other §4.1 validators).
 *
 * `activeBackend` is **not** checked against the installed registry here — this package knows no
 * backends. An id whose backend was uninstalled decodes fine and surfaces as `ready: false` on #77.
 *
 * Its fallback is the shipped default, not `null`, so that a blob missing the field and a blob that
 * is not an object at all land on the same value; `null` is preserved when it is actually stored,
 * because that is the user having chosen *None (disabled)* through #80, not a decode failure.
 */
const StoredAiSettingsSchema = z.object({
    activeBackend: z
        .string()
        .min(1)
        .nullable()
        .catch(AI_DEFAULT_BACKEND_ID)
        .default(AI_DEFAULT_BACKEND_ID),
    systemPrompt: storedPrompt(AI_DEFAULT_SYSTEM_PROMPT),
    contextTemplate: storedPrompt(AI_DEFAULT_CONTEXT_TEMPLATE),
    // Whole-field fallback, not per-entry: a half-decoded preset list would silently drop the button
    // the user is looking for, and a corrupt list is not a list. A stored `[]` is *not* corruption —
    // it is the user having deleted every preset — so it survives.
    presets: z
        .array(StoredPresetSchema)
        .max(AI_PRESETS_MAX)
        .catch(() => aiDefaultPresets())
        .default(() => aiDefaultPresets()),
});
/**
 * Read-time rename of the pre-presets blob: `userPromptTemplate` became `contextTemplate` when the
 * post context moved from the first user message into the system prompt. The text is the user's, so
 * it is carried over rather than dropped; the legacy key disappears from storage on the next #81
 * save, which writes the whole decoded shape back.
 */
function migrateLegacyTemplate(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return value;
    const blob = value;
    if (!('userPromptTemplate' in blob) || 'contextTemplate' in blob)
        return value;
    return { ...blob, contextTemplate: blob['userPromptTemplate'] };
}
export function decodeAiSettings(value) {
    const parsed = StoredAiSettingsSchema.safeParse(migrateLegacyTemplate(value));
    return parsed.success ? parsed.data : aiSettingsDefault();
}
/* ------------------------------------------------------------------ #82 chat */
export const AI_CHAT_ROLES = ['user', 'assistant'];
/* ---------------------------------------------------------------- #78 config */
/**
 * Partial config write, #36 semantics exactly: values arrive in their declared runtime type (the
 * form coerces), a secret left untouched is omitted and keeps its stored value, `''` clears.
 */
export const AiConfigUpdateBodySchema = z.strictObject({
    config: z.record(z.string().min(1).max(100), z.union([z.string(), z.number(), z.boolean()])),
});
/* ---------------------------------------------------------------- #80 active */
/** `null` disables the feature; an id the registry does not know is a 400 at the route. */
export const AiActiveUpdateBodySchema = z.strictObject({
    backendId: z.string().min(1).max(100).nullable(),
});
/* --------------------------------------------------------------- #81 prompts */
/**
 * The whole block, always — the tab holds one draft and one Save, so a partial patch has no meaning.
 * Everything is trimmed before validation *and* before storage: leading/trailing whitespace in a
 * prompt is never intentional, and storing the trimmed form keeps the decoder's non-empty rule
 * honest.
 */
const PromptSchema = z.string().trim().min(1).max(AI_PROMPT_MAX_CHARS);
/** A preset's prompt is capped at `AI_MESSAGE_MAX_CHARS` because it literally becomes one (#83/#84). */
export const AiPresetBodySchema = z.strictObject({
    name: z.string().trim().min(1).max(AI_PRESET_NAME_MAX_CHARS),
    prompt: z.string().trim().min(1).max(AI_MESSAGE_MAX_CHARS),
});
export const AiPromptsUpdateBodySchema = z.strictObject({
    systemPrompt: PromptSchema,
    contextTemplate: PromptSchema,
    /** An empty list is accepted: deleting every preset is a choice, not an error. */
    presets: z.array(AiPresetBodySchema).max(AI_PRESETS_MAX),
});
/* --------------------------------------------------------- #83/#84 message */
/**
 * One user turn — the first one (#83) and every follow-up (#84) alike. Trimmed then bounded, so a
 * message of pure whitespace is a 400 rather than an empty turn. The bound is on what the *user*
 * sends: `{{...}}` tokens in it are rendered server-side afterwards, and a body that renders to
 * nothing is refused there (before anything is persisted), not here.
 */
export const AiMessageBodySchema = z.strictObject({
    content: z.string().trim().min(1).max(AI_MESSAGE_MAX_CHARS),
});
//# sourceMappingURL=ai.js.map